import { Select } from 'antd';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ClearIcon, SuffixIcon } from 'components/SelectChooser/SelectChooser.style';
import {
  type AtlasGqlTurbine,
  AtlasGqlFilterOperator,
  useGetSiteTurbinesForChooserQuery,
} from 'types/atlas-graphql';
import { naturalSortBy } from 'utils/naturalSort';

import { useAssetChooserState } from '../AssetChooser/useAssetChooserState';
import { filterOption } from '../helpers';

import { type LocationTurbineChooserComponentProps } from './types';

export function SingleLocationTurbineChooser({
  value,
  onChange,
  mode,
  locationId,
  // The disabled parameter will prevent loading select options, this disables the input element.
  disableInput = false,
}: LocationTurbineChooserComponentProps) {
  const { t } = useTranslation();
  const { handleChange, setVisible, chooserState } = useAssetChooserState({
    onChange,
    value,
  });

  const [turbines, setTurbines] = useState<Array<AtlasGqlTurbine>>([]);
  // Keep search text across selections so users can bulk-select from a filtered list.
  // Clear when the dropdown closes (done selecting / dismissed the field).
  const [searchValue, setSearchValue] = useState('');

  const { data, loading } = useGetSiteTurbinesForChooserQuery({
    variables: {
      filterBy: [
        { key: 'childrenOfIds', operator: AtlasGqlFilterOperator.Equals, values: [locationId] },
        { key: 'assetTypeNames', operator: AtlasGqlFilterOperator.Equals, values: ['turbine'] },
      ],
    },
  });

  useEffect(() => {
    if (data?.assetsWithMetadata?.items) {
      const newTurbines = data?.assetsWithMetadata?.items;
      // You have to call sort on a copy of newTurbines, rather than on the array itself.
      const sortedTurbines = [...newTurbines].sort(naturalSortBy('name'));
      setTurbines(sortedTurbines as AtlasGqlTurbine[]);
    }
  }, [data]);

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setSearchValue('');
    }
    setVisible(open);
  };

  return (
    <Select
      id="single-location-turbine-chooser"
      data-testid="single-location-turbine-chooser"
      placeholder={t('table.selectTurbines', 'Select Turbines...')}
      loading={loading}
      allowClear={{ clearIcon: <ClearIcon /> }}
      showSearch
      filterOption={filterOption}
      mode={mode}
      maxTagCount={2}
      value={loading ? undefined : chooserState}
      onChange={handleChange}
      onOpenChange={handleOpenChange}
      disabled={disableInput}
      suffixIcon={chooserState ? null : <SuffixIcon />}
      autoClearSearchValue={false}
      searchValue={searchValue}
      onSearch={setSearchValue}
      options={turbines.map(turbine => ({
        className: 'single-location-turbine-chooser-option',
        value: turbine.id,
        label: turbine.name,
      }))}
    />
  );
}
